import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchWallet24hProfitAndLoss,
  fetchWallet7dHistoricalPnL,
} from '#/api/apiExchange';
import { useWebsocketEventListener } from '#/components/hooks';
import { useSelector } from '#/reduxState/store';

import { minutes, seconds } from './utils';

const useInvalidatePnLQueries = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['walletHistoricalPnL24h'],
    });
  };
};

/**
 * We have to invalidate date on deposit/withdrawals to prevent displaying incorrect PnL.
 * Alternativelly we can use apply changed quantities.
 */
export const useGlobalPositionPnlQueriesInvalidator = () => {
  const invalidateQueries = useInvalidatePnLQueries();

  useWebsocketEventListener('deposits', () => {
    invalidateQueries();
  });
  useWebsocketEventListener('withdrawals', () => {
    invalidateQueries();
  });
};

export const useLast24hWalletPnLQuery = () => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  return useQuery<{
    firstTotalPnL: number;
    lastTotalPnl: number;
    firstNonZeroEquity: number;
    lastEquity: number;
  }>({
    queryKey: ['walletHistoricalPnL24h', walletAddress],
    queryFn: !walletAddress
      ? skipToken
      : async () => fetchWallet24hProfitAndLoss(walletAddress),
    staleTime: minutes(3),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(5),
  });
};

export const use7daysPortfolioPnlChartQuery = ({
  walletAddress,
  isVault,
  endTime,
}: {
  walletAddress: string;
  isVault: boolean;
  /** End of the 7 day window; defaults to now (eg. liquidation time for liquidated vaults) */
  endTime?: number;
}) => {
  return useQuery({
    queryKey: ['walletPortfolioPnLChart', walletAddress, isVault, endTime],
    queryFn: !walletAddress
      ? skipToken
      : () => fetchWallet7dHistoricalPnL(walletAddress, isVault, endTime),
    staleTime: endTime ? Infinity : seconds(30),
    refetchOnWindowFocus: false,
    // Fixed historical window (ended vault) never changes - no need to refetch
    refetchInterval: endTime ? false : minutes(2),
  });
};
